import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import '../../src/layouts/styles/contact.css';
import { EnquiryPublicProvider, useEnquiryPublic, useEnquiryPublicActions } from '../modules/contact/context/EnquiryPublicContext';
import { enquirySchema, EnquiryFormData } from '../modules/contact/validation/enquirySchemas';
import SeoHead from '../shared/seo/SeoHead';
import { buildLocalBusinessSchema, buildBreadcrumbSchema } from '../shared/seo/schemas';
import { SITE_BASE } from '../shared/seo/siteUrl';

// ── Internal form component — consumes context ───────────────

const ContactFormSection: React.FC = () => {
    const { loading, submitted, error, referenceId } = useEnquiryPublic();
    const { submitEnquiry, reset } = useEnquiryPublicActions();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset: resetForm,
    } = useForm<EnquiryFormData>({ resolver: zodResolver(enquirySchema) });

    const onSubmit = async (data: EnquiryFormData) => {
        await submitEnquiry(data);
        resetForm();
    };

    if (submitted) {
        return (
            <div className="ct-success">
                <div className="ct-success-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                </div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                {referenceId && (
                    <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                        Reference: <strong style={{ color: '#1565c0' }}>{referenceId}</strong>
                    </p>
                )}
                <button className="ct-reset-btn" onClick={reset}>
                    Send Another Message
                </button>
            </div>
        );
    }

    return (
        <form className="ct-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {error && (
                <div className="ct-form-api-error">{error}</div>
            )}

            <div className="ct-row">
                <div className="ct-field">
                    <label htmlFor="ct-name">Full Name <span>*</span></label>
                    <input
                        id="ct-name"
                        type="text"
                        placeholder="Rahul Sharma"
                        {...register('name')}
                    />
                    {errors.name && <p className="ct-field-error">{errors.name.message}</p>}
                </div>
                <div className="ct-field">
                    <label htmlFor="ct-mobile">Phone Number <span>*</span></label>
                    <input
                        id="ct-mobile"
                        type="tel"
                        placeholder="+91 98765 43210"
                        {...register('mobile')}
                    />
                    {errors.mobile && <p className="ct-field-error">{errors.mobile.message}</p>}
                </div>
            </div>

            <div className="ct-field">
                <label htmlFor="ct-email">Email Address <span>*</span></label>
                <input
                    id="ct-email"
                    type="email"
                    placeholder="rahul@example.com"
                    {...register('email')}
                />
                {errors.email && <p className="ct-field-error">{errors.email.message}</p>}
            </div>

            <div className="ct-field">
                <label htmlFor="ct-category">Subject <span>*</span></label>
                <select id="ct-category" {...register('category')}>
                    <option value="">Select a topic…</option>
                    <option value="bulk-order">Bulk Order Enquiry</option>
                    <option value="custom-craft">Custom Craft Request</option>
                    <option value="product-info">Product Information</option>
                    <option value="pricing">Pricing & Quotation</option>
                    <option value="shipping">Shipping & Delivery</option>
                    <option value="other">Other</option>
                </select>
                {errors.category && <p className="ct-field-error">{errors.category.message}</p>}
            </div>

            <div className="ct-field">
                <label htmlFor="ct-description">Message <span>*</span></label>
                <textarea
                    id="ct-description"
                    rows={5}
                    placeholder="Tell us about your requirement — quantity, product type, delivery timeline…"
                    {...register('description')}
                />
                {errors.description && <p className="ct-field-error">{errors.description.message}</p>}
            </div>

            <button type="submit" className="ct-submit-btn" disabled={loading}>
                {loading ? (
                    <>
                        <span className="ct-spinner" />
                        Sending…
                    </>
                ) : (
                    <>
                        Send Message
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                        </svg>
                    </>
                )}
            </button>
        </form>
    );
};

// ── Main page component ──────────────────────────────────────

const Contact = () => (
    <EnquiryPublicProvider>
        <SeoHead
            title="Contact Arte Craft India | Bulk Orders & Custom Craft Enquiries Delhi"
            description="Contact Arte Craft India for pom pom, tassel, fringe, and faux fur bulk orders. Call +91-9818548369 or email info@artecraftindia.com. Reply within 24 hours."
            keywords="contact Arte Craft India, pom pom bulk order enquiry, tassel manufacturer contact Delhi, craft accessories wholesale enquiry, custom order India"
            canonical={`${SITE_BASE}/contact`}
            author="Arte Craft India"
            robots="index, follow"
            ogType="website"
            ogUrl={`${SITE_BASE}/contact`}
            ogSiteName="Arte Craft India"
            ogImage={`${SITE_BASE}/images/homepage-og.jpg`}
            ogImageAlt="Contact Arte Craft India — pom poms and tassels manufacturer in New Delhi"
            ogLocale="en_IN"
            twitterCard="summary_large_image"
            twitterSite="@ArteCraftIndia"
            twitterImage={`${SITE_BASE}/images/homepage-twitter.jpg`}
            geoRegion="IN-DL"
            geoPlacename="Badarpur, New Delhi, India"
            geoPosition="28.5045;77.2855"
            jsonLd={[
                buildLocalBusinessSchema(),
                buildBreadcrumbSchema([
                    { name: "Home", url: `${SITE_BASE}/` },
                    { name: "Contact", url: `${SITE_BASE}/contact` },
                ]),
            ]}
        />
        <main className="ct-page">

            {/* ── Hero ── */}
            <section className="ct-hero">
                <div className="ct-hero-bg" aria-hidden="true" />
                <div className="ct-hero-inner">
                    <span className="ct-hero-tag">Contact Us</span>
                    <h1 className="ct-hero-title">Let's Start a Conversation</h1>
                    <p className="ct-hero-sub">
                        Reach out for bulk orders, custom craft queries, or general enquiries.
                        We reply within 24 hours.
                    </p>
                </div>
            </section>

            {/* ── Quick info strip ── */}
            <div className="ct-strip">
                <div className="ct-strip-container">
                    <div className="ct-strip-item">
                        <span className="ct-strip-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                        </span>
                        <div>
                            <div className="ct-strip-label">Working Hours</div>
                            <div className="ct-strip-val">Mon – Sun, 10 AM – 10 PM</div>
                        </div>
                    </div>
                    <div className="ct-strip-divider" />
                    <div className="ct-strip-item">
                        <span className="ct-strip-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16h1z"/>
                            </svg>
                        </span>
                        <div>
                            <div className="ct-strip-label">Call Us</div>
                            <div className="ct-strip-val">+91 98185 48369</div>
                        </div>
                    </div>
                    <div className="ct-strip-divider" />
                    <div className="ct-strip-item">
                        <span className="ct-strip-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </svg>
                        </span>
                        <div>
                            <div className="ct-strip-label">Email Us</div>
                            <div className="ct-strip-val">info@artecraftindia.com</div>
                        </div>
                    </div>
                    <div className="ct-strip-divider" />
                    <div className="ct-strip-item">
                        <span className="ct-strip-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                        </span>
                        <div>
                            <div className="ct-strip-label">Visit Us</div>
                            <div className="ct-strip-val">Badarpur, New Delhi</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Main content ── */}
            <section className="ct-main">
                <div className="ct-container">
                    <div className="ct-grid">

                        {/* ── Contact form ── */}
                        <div className="ct-form-card">
                            <div className="ct-card-head">
                                <h2>Send Us a Message</h2>
                                <p>Fill in the form and our team will get back to you within 24 hours.</p>
                            </div>
                            <ContactFormSection />
                        </div>

                        {/* ── Info sidebar ── */}
                        <div className="ct-info-col">

                            {/* Address card */}
                            <div className="ct-info-card">
                                <div className="ct-info-icon ct-info-icon--blue">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                        <circle cx="12" cy="10" r="3"/>
                                    </svg>
                                </div>
                                <div>
                                    <h4>Our Address</h4>
                                    <p>280/13 Gali No. 29, Molarband Extn,<br />Badarpur, New Delhi – 110044</p>
                                </div>
                            </div>

                            {/* Phone card */}
                            <div className="ct-info-card">
                                <div className="ct-info-icon ct-info-icon--green">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16h1z"/>
                                    </svg>
                                </div>
                                <div>
                                    <h4>Phone Numbers</h4>
                                    <p>
                                        <a href="tel:+919818548369">+91 98185 48369</a><br />
                                        <a href="tel:+919818548369">+91 98185 48369</a>
                                    </p>
                                </div>
                            </div>

                            {/* Email card */}
                            <div className="ct-info-card">
                                <div className="ct-info-icon ct-info-icon--orange">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                                    </svg>
                                </div>
                                <div>
                                    <h4>Email</h4>
                                    <p><a href="mailto:info@artecraftindia.com">info@artecraftindia.com</a></p>
                                </div>
                            </div>

                            {/* WhatsApp CTA */}
                            <a
                                href="https://wa.me/919818548369"
                                target="_blank"
                                rel="noreferrer"
                                className="ct-wa-btn"
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                    <path d="M11.999 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.948-1.42A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
                                </svg>
                                Chat on WhatsApp
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:"auto"}}>
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </a>

                            {/* Hours card */}
                            <div className="ct-hours-card">
                                <div className="ct-hours-head">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    Business Hours
                                </div>
                                <div className="ct-hours-row">
                                    <span>Monday – Sunday</span>
                                    <span className="ct-hours-val">10:00 AM – 10:00 PM</span>
                                </div>
                                <div className="ct-hours-badge">
                                    <span className="ct-dot" /> Open Today
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* ── Map / location section ── */}
            <section className="ct-map-section">
                <div className="ct-container">
                    <div className="ct-map-header">
                        <h2>Find Us</h2>
                        <p>280/13 Gali No. 29, Molarband Extension, Badarpur, New Delhi – 110044</p>
                    </div>

                    <div className="ct-map-actions">
                        <a
                            href="https://www.google.com/maps/dir/?api=1&destination=Arte+Craft+India&destination_place_id=0x390ce76fb5b63099:0x38350c203bb02335"
                            target="_blank"
                            rel="noreferrer"
                            className="ct-map-action-btn ct-map-action-btn--primary"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="3 11 22 2 13 21 11 13 3 11"/>
                            </svg>
                            Get Directions
                        </a>
                        <a
                            href="https://www.google.com/maps/place/Arte+Craft+India/@28.4945238,77.3140334,17z/data=!3m1!4b1!4m6!3m5!1s0x390ce76fb5b63099:0x38350c203bb02335!8m2!3d28.4945238!4d77.3140334"
                            target="_blank"
                            rel="noreferrer"
                            className="ct-map-action-btn"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            Open in Google Maps
                        </a>
                    </div>

                    <div className="ct-map-wrap">
                        <iframe
                            title="Arte Craft India — Molarband Extension, Badarpur, New Delhi"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3508.9287344!2d77.3118447!3d28.4945238!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce76fb5b63099%3A0x38350c203bb02335!2sArte%20Craft%20India!5e0!3m2!1sen!2sin!4v1715000000000!5m2!1sen!2sin"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </section>

        </main>
    </EnquiryPublicProvider>
);

export default Contact;
